<template>
  <div class="og-faq">
    <div
      v-for="(item, index) in faqs"
      :key="index"
      class="og-faq__item"
      :class="{ 'og-faq__item--open': activeIndex === index }"
    >
      <button
        type="button"
        class="og-faq__trigger"
        :aria-expanded="activeIndex === index ? 'true' : 'false'"
        :aria-controls="`og-faq-panel-${index}`"
        :id="`og-faq-trigger-${index}`"
        @click="toggleFaq(index)"
      >
        <span class="og-faq__trigger-inner">
          <span class="og-faq__badge" aria-hidden="true">{{ index + 1 }}</span>
          <span class="og-faq__question">{{ item.question }}</span>
        </span>
        <span class="og-faq__chevron-wrap" aria-hidden="true">
          <svg
            class="og-faq__chevron"
            :class="{ 'og-faq__chevron--open': activeIndex === index }"
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 9l6 6 6-6"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
      </button>

      <transition name="og-faq-expand">
        <div
          v-show="activeIndex === index"
          :id="`og-faq-panel-${index}`"
          role="region"
          :aria-labelledby="`og-faq-trigger-${index}`"
          class="og-faq__panel"
        >
          <div class="og-faq__answer" v-html="item.answer" />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    faqs: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      activeIndex: null,
    };
  },
  methods: {
    toggleFaq(index) {
      if (this.activeIndex === index) {
        this.activeIndex = null;
      } else {
        this.activeIndex = index;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* FAQ 内容卡片：白底 + #E5E5E5 边线（ogwta 清单） */
.og-faq {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.og-faq__item {
  border-radius: 4px;
  border: 1px solid #e5e5e5;
  background: #ffffff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.og-faq__item:hover {
  border-color: rgba(32, 79, 70, 0.35);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.og-faq__item--open {
  border-color: rgba(52, 211, 153, 0.55);
  box-shadow: 0 6px 18px rgba(32, 79, 70, 0.08);
}

.og-faq__trigger {
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 16px 18px;
  text-align: left;
  font: inherit;
  color: #333333;
}

.og-faq__trigger:hover .og-faq__question {
  color: #204f46;
}

.og-faq__trigger:focus {
  outline: none;
}

.og-faq__trigger:focus-visible {
  outline: 2px solid #34d399;
  outline-offset: 2px;
}

.og-faq__trigger-inner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.og-faq__badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin-top: 2px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 800;
  color: #ffffff;
  background: #204f46;
  border: none;
  box-shadow: none;
}

.og-faq__item--open .og-faq__badge {
  background: #34d399;
  border: none;
}

.og-faq__question {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.45;
  color: #333333;
  transition: color 0.15s ease;
}

.og-faq__chevron-wrap {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  color: #204f46;
}

.og-faq__item--open .og-faq__chevron-wrap {
  background: #ffffff;
  border-color: #34d399;
  color: #34d399;
}

.og-faq__chevron {
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

.og-faq__chevron--open {
  transform: rotate(180deg);
}

.og-faq__panel {
  border-top: 1px solid #e5e5e5;
  background: #ffffff;
}

.og-faq__answer {
  padding: 16px 18px 18px 56px;
  font-size: 0.95rem;
  line-height: 1.65;
  color: #333333;
}

@media (max-width: 520px) {
  .og-faq__answer {
    padding-left: 18px;
    padding-top: 12px;
  }

  .og-faq__badge {
    margin-top: 0;
  }
}

.og-faq__answer ::v-deep a {
  color: #204f46 !important;
  font-weight: 600;
  text-decoration: none !important;
  text-underline-offset: 2px;
}

.og-faq__answer ::v-deep a:hover {
  color: #34d399 !important;
  text-decoration: underline !important;
  text-decoration-thickness: 1px;
}

.og-faq__answer ::v-deep strong {
  color: #333333;
  font-weight: 700;
}

.og-faq-expand-enter-active,
.og-faq-expand-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.og-faq-expand-enter,
.og-faq-expand-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
